import { useEffect, useRef } from "react";

/**
 * BinauralHUD — Frequency-map style HUD overlay rendered on top of the 3D brain canvas.
 * Same visual language as the BrainLattice radar interface (range rings, sweep beam,
 * compass reticle, scanline, corner brackets, telemetry, spectrum bar, band meters).
 *
 * Props:
 *   - activeFreq: number  (carrier in Hz)
 *   - beatHz: number      (binaural delta in Hz)
 *   - energy: number      (0-1 envelope)
 *   - sessionOn: boolean  (live state)
 *   - presetLabel: string ("ALPHA", "THETA" etc — for top-left status)
 */
export default function BinauralHUD({ activeFreq, beatHz, energy, sessionOn, presetLabel = "—" }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const sweepRef = useRef(0);
  const starsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      if (starsRef.current.length === 0) {
        for (let i = 0; i < 60; i++) {
          starsRef.current.push({
            x: Math.random(),
            y: Math.random(),
            r: Math.random() * 1.0 + 0.2,
            tw: Math.random() * Math.PI * 2,
            sp: 0.05 + Math.random() * 0.15,
          });
        }
      }
    };
    resize();

    let t = 0;

    // Determine dominant brainwave band from beatHz
    const bandFor = (hz) => {
      if (hz < 4) return { label: "DELTA", color: "#4A90E2", glyph: "δ" };
      if (hz < 8) return { label: "THETA", color: "#9B59B6", glyph: "θ" };
      if (hz < 13) return { label: "ALPHA", color: "#F1C40F", glyph: "α" };
      if (hz < 30) return { label: "BETA", color: "#E67E22", glyph: "β" };
      return { label: "GAMMA", color: "#E74C3C", glyph: "γ" };
    };

    const draw = () => {
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      ctx.clearRect(0, 0, w, h);
      t += 0.016;
      const band = bandFor(beatHz);
      const cx = w * 0.5, cy = h * 0.5;

      // ── Tech grid (very faint, behind 3D brain — minimal so it doesn't fight the canvas)
      ctx.strokeStyle = "rgba(255,0,0,0.05)";
      ctx.lineWidth = 0.5;
      const cols = 16, rows2 = 9;
      for (let i = 0; i <= cols; i++) {
        const x = (i / cols) * w;
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
      }
      for (let i = 0; i <= rows2; i++) {
        const y = (i / rows2) * h;
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
      }

      // ── Twinkling stars
      for (const s of starsRef.current) {
        s.tw += s.sp * 0.04;
        const tw = (Math.sin(s.tw) * 0.5 + 0.5);
        ctx.fillStyle = `rgba(255, 50, 50, ${0.05 + tw * 0.20})`;
        ctx.beginPath();
        ctx.arc(s.x * w, s.y * h, s.r * (0.7 + tw * 0.6), 0, Math.PI * 2);
        ctx.fill();
      }

      // ── Range rings + cardinal markers
      const maxR = Math.min(w, h) * 0.46;
      const ringIntensity = sessionOn ? 0.10 + Math.min(0.20, energy * 0.30) : 0.06;
      for (let k = 1; k <= 5; k++) {
        const rr = (k / 5) * maxR;
        ctx.strokeStyle = `rgba(255,0,0,${ringIntensity / k * 0.9})`;
        ctx.lineWidth = k === 3 ? 1 : 0.4;
        ctx.beginPath();
        ctx.arc(cx, cy, rr, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.fillStyle = "rgba(255,0,0,0.30)";
      ctx.font = "7px monospace";
      ctx.textAlign = "left";
      for (let k = 1; k <= 5; k++) {
        const rr = (k / 5) * maxR;
        ctx.fillText(`R${k}`, cx + rr + 3, cy - 2);
      }
      ctx.fillStyle = "rgba(255,0,0,0.45)";
      ctx.font = "bold 8px monospace";
      ctx.textAlign = "center";
      ctx.fillText("N", cx, cy - maxR - 6);
      ctx.fillText("S", cx, cy + maxR + 12);
      ctx.textAlign = "left";
      ctx.fillText("E", cx + maxR + 4, cy + 3);
      ctx.textAlign = "right";
      ctx.fillText("W", cx - maxR - 4, cy + 3);

      // ── Rotating sweep beam (faster when session is on)
      sweepRef.current = (sweepRef.current + (sessionOn ? 0.014 : 0.006)) % (Math.PI * 2);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(sweepRef.current);
      const sweepGrad = ctx.createLinearGradient(0, 0, maxR, 0);
      sweepGrad.addColorStop(0, sessionOn ? "rgba(255,30,30,0.55)" : "rgba(255,30,30,0.25)");
      sweepGrad.addColorStop(0.4, "rgba(255,0,0,0.12)");
      sweepGrad.addColorStop(1, "rgba(255,0,0,0)");
      ctx.strokeStyle = sweepGrad;
      ctx.lineWidth = 1.4;
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(maxR, 0); ctx.stroke();
      ctx.fillStyle = "rgba(255,0,0,0.04)";
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, maxR, -0.35, 0, false);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // ── Scanline
      ctx.save();
      ctx.globalAlpha = 0.06;
      for (let y = 0; y < h; y += 3) {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, y, w, 1);
      }
      ctx.restore();

      // ── Bottom spectrum bar — 16 channels driven by beatHz harmonic phase + energy
      const specH = 32;
      const specY = h - specH - 6;
      const specPad = 12;
      ctx.save();
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(specPad, specY, w - specPad * 2, specH);
      ctx.strokeStyle = "rgba(255,0,0,0.35)";
      ctx.lineWidth = 0.8;
      ctx.strokeRect(specPad, specY, w - specPad * 2, specH);
      const barCount = 16;
      const usableW = w - specPad * 2 - 4;
      const barW = usableW / barCount;
      for (let i = 0; i < barCount; i++) {
        const phase = Math.sin(t * (3 + beatHz * 0.05) + i * 0.6);
        const lvl = sessionOn ? 0.30 + (phase * 0.35 + 0.65) * 0.55 * (0.5 + energy * 0.7) : 0.05 + Math.abs(Math.sin(t * 0.6 + i)) * 0.04;
        const bx = specPad + 2 + i * barW;
        const bh = lvl * (specH - 6);
        const barGrad = ctx.createLinearGradient(0, specY + specH - bh, 0, specY + specH);
        barGrad.addColorStop(0, sessionOn ? band.color : "rgba(255,0,0,0.30)");
        barGrad.addColorStop(1, sessionOn ? band.color + "55" : "rgba(255,0,0,0.06)");
        ctx.fillStyle = barGrad;
        ctx.fillRect(bx, specY + specH - 3 - bh, barW - 1.5, bh);
        if (sessionOn) {
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(bx, specY + specH - 3 - bh - 1, barW - 1.5, 1);
        }
      }
      ctx.fillStyle = "rgba(255,0,0,0.45)";
      ctx.font = "7px monospace";
      ctx.textAlign = "left";
      ctx.fillText("SPECTRUM · BINAURAL ENV", specPad + 2, specY - 3);
      ctx.textAlign = "right";
      ctx.fillText(`${barCount} CH`, w - specPad - 2, specY - 3);
      ctx.restore();

      // ── Left-side brainwave band meters (highlight current band)
      const bandList = [
        { key: "GAMMA", color: "#E74C3C", glyph: "γ" },
        { key: "BETA",  color: "#E67E22", glyph: "β" },
        { key: "ALPHA", color: "#F1C40F", glyph: "α" },
        { key: "THETA", color: "#9B59B6", glyph: "θ" },
        { key: "DELTA", color: "#4A90E2", glyph: "δ" },
      ];
      ctx.save();
      const meterX = 8, meterY0 = 60, meterH = 32, meterW = 4, meterGap = 5;
      bandList.forEach((b, i) => {
        const y0 = meterY0 + i * (meterH + meterGap);
        const isActive = sessionOn && b.key === band.label;
        const fill = isActive ? 0.85 + Math.sin(t * 3) * 0.1 : 0;
        ctx.fillStyle = "rgba(0,0,0,0.5)";
        ctx.fillRect(meterX, y0, meterW, meterH);
        ctx.strokeStyle = b.color + "55";
        ctx.lineWidth = 0.5;
        ctx.strokeRect(meterX, y0, meterW, meterH);
        if (fill > 0) {
          ctx.fillStyle = b.color;
          ctx.fillRect(meterX, y0 + meterH - fill * meterH, meterW, fill * meterH);
        }
        ctx.fillStyle = isActive ? b.color : b.color + "40";
        ctx.font = "bold 10px monospace";
        ctx.textAlign = "left";
        ctx.fillText(b.glyph, meterX + meterW + 3, y0 + meterH - 2);
      });
      ctx.restore();

      // ── Top-left: PROTOCOL info
      ctx.save();
      ctx.font = "8px monospace";
      ctx.textAlign = "left";
      ctx.fillStyle = "rgba(255,0,0,0.55)";
      ctx.fillText("◉ PROTOCOL", 22, 14);
      ctx.fillStyle = "rgba(255,0,0,0.95)";
      ctx.fillText(presetLabel, 22, 26);
      ctx.fillStyle = "rgba(255,0,0,0.50)";
      ctx.fillText(`CARRIER  ${Number(activeFreq).toFixed(1)} Hz`, 22, 36);
      ctx.fillText(`BEAT     ${Number(beatHz).toFixed(2)} Hz`, 22, 46);
      ctx.restore();

      // ── Top-right: TELEMETRY
      ctx.save();
      ctx.font = "8px monospace";
      ctx.textAlign = "right";
      ctx.fillStyle = "rgba(255,0,0,0.55)";
      ctx.fillText("◉ TELEMETRY", w - 8, 14);
      ctx.fillStyle = sessionOn ? "rgba(255,0,0,0.95)" : "rgba(255,0,0,0.30)";
      ctx.fillText(`BAND: ${band.label}`, w - 8, 26);
      ctx.fillText(`PINEAL: ${sessionOn ? "RESONATING" : "STANDBY"}`, w - 8, 36);
      ctx.fillText(`ENGY: ${Math.round(energy * 100)}%`, w - 8, 46);
      const hbPulse = Math.sin(t * (sessionOn ? 4 : 1.6)) * 0.4 + 0.6;
      ctx.fillStyle = `rgba(255,0,0,${0.4 + hbPulse * 0.6})`;
      ctx.beginPath();
      ctx.arc(w - 12, 11, 2.2 + hbPulse * 0.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // ── Center crosshair (compass reticle behind brain — subtle)
      ctx.save();
      ctx.strokeStyle = "rgba(255,0,0,0.20)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cx - 12, cy); ctx.lineTo(cx - 4, cy);
      ctx.moveTo(cx + 4, cy);  ctx.lineTo(cx + 12, cy);
      ctx.moveTo(cx, cy - 12); ctx.lineTo(cx, cy - 4);
      ctx.moveTo(cx, cy + 4);  ctx.lineTo(cx, cy + 12);
      ctx.stroke();
      ctx.restore();

      // ── Bottom-left: tuning / SR
      ctx.save();
      ctx.font = "7px monospace";
      ctx.textAlign = "left";
      ctx.fillStyle = "rgba(255,0,0,0.40)";
      ctx.fillText("HRTF  SPATIAL", 22, specY - 12);
      ctx.fillText("SR    44.1kHz", 22, specY - 4);
      ctx.restore();

      // ── Corner HUD brackets
      ctx.save();
      ctx.strokeStyle = "rgba(255,0,0,0.5)";
      ctx.lineWidth = 1.2;
      const bL = 14;
      ctx.beginPath(); ctx.moveTo(2, bL); ctx.lineTo(2, 2); ctx.lineTo(bL, 2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(w - bL, 2); ctx.lineTo(w - 2, 2); ctx.lineTo(w - 2, bL); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(2, h - bL); ctx.lineTo(2, h - 2); ctx.lineTo(bL, h - 2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(w - bL, h - 2); ctx.lineTo(w - 2, h - 2); ctx.lineTo(w - 2, h - bL); ctx.stroke();
      ctx.restore();

      frameRef.current = requestAnimationFrame(draw);
    };

    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(frameRef.current); window.removeEventListener("resize", resize); };
  }, [activeFreq, beatHz, energy, sessionOn, presetLabel]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 10 }}
      data-testid="binaural-hud"
    />
  );
}
